import {Injectable, inject} from '@angular/core';
import {BehaviorSubject, Observable, Subject, Subscription} from 'rxjs';
import {
  CropIdentificationService
} from '@6grain/masofa-monolith-api-client/ai-inference/api/cropIdentification.service';
import {
  CropIdentificationMapResponse
} from '@6grain/masofa-monolith-api-client/ai-inference/model/cropIdentificationMapResponse';
import {OpenStreetMapComponent} from '@components/openstreetmap/openstreetmap.component';
import {CropCacheService} from '@services/caches/crop-cache.service';
import {PolygonConverterUtil} from '@app/utils/polygon-converter.util';
import {GisCircleMarker} from '@app/pages/electronic-field-map/pages/gis-fields/interfaces/gis-component.interfaces';
import {GisPolygon} from '@components/openstreetmap/interfaces/gis-component.interfaces';
import {Marker} from '@components/openstreetmap/interfaces/marker-layer.interface';
import {TranslateService} from '@ngx-translate/core';
import {tap} from 'rxjs/operators';
import {CropIdentificationMapItemDto, MapClusterDto} from '@6grain/masofa-monolith-api-client/ai-inference';
@Injectable({providedIn: 'root'})
export class CropIaManager {
  public cropIdentificationService = inject(CropIdentificationService);
  private cropCacheService = inject(CropCacheService);
  private mapInstance: OpenStreetMapComponent | null = null;
  private translate = inject(TranslateService);

  // Store data
  public cropIaData$ = new BehaviorSubject<CropIdentificationMapResponse | null>(null);

  // Events
  public cropIaMarkerClick$ = new Subject<{ lat: number; lng: number; data: any }>();
  public cropIaPolygonClick$ = new Subject<any>();
  clusterGroupId: string | null = null;
  /** General GIS: dedicated Leaflet panes and layer swatch (clusters); polygons stay crop-colored. */
  private generalGisPolygonPane: string | null = null;
  private generalGisClusterPane: string | null = null;
  private layerAccentColor: string | null = null;
  private polygonFillOpacityPercent: number | null = null;

  // Track markers and polygons added by this manager
  private managedMarkerIds: Set<string> = new Set();
  private managedPolygonIds: Set<string | number> = new Set();
  private managedClusters: Set<string> = new Set();

  private cropIaDataSub: Subscription | null = null;
  private langChangeSub: Subscription | null = null;
  /** When set, auto-open Leaflet popup after the next {@link renderCropPolygons}. */
  private pendingFocusPopupId: string | null = null;
  private NO_CROP_POLYGON_COLOR = '#4287f5';
  private readonly MATCH_STROKE = '#22c55e';
  private readonly MISMATCH_STROKE = '#ef4444';

  private getPlannedCultureLabel(): string {
    return this.translate.instant('CROP_IA_PLAN_LABEL');
  }

  private getIaLabel(): string {
    return this.translate.instant('CROP_IA_AI_LABEL');
  }

  private formatIaProbabilityPercent(iaProbability: number): string {
    return `${(iaProbability * 100).toFixed(2)}%`;
  }

  private buildPlannedVsIaLabel(plannedName: string, iaName: string, iaProbability?: number | null): string {
    const planned = `${this.getPlannedCultureLabel()}: ${plannedName || '-'}`;
    const percent =
      typeof iaProbability === 'number' && !Number.isNaN(iaProbability)
        ? ` (${this.formatIaProbabilityPercent(iaProbability)})`
        : '';
    const ia = `${this.getIaLabel()}: ${iaName || '-'}${percent}`;
    return `${planned}<br/>${ia}`;
  }

  private shouldShowIaLabel(item: CropIdentificationMapItemDto, fallbackProb?: number | null): boolean {
    const top = Number((item as { topProbability?: number | null })?.topProbability);
    if (!Number.isNaN(top)) {
      return top > 0;
    }
    const score = Number(fallbackProb);
    return !Number.isNaN(score) && score > 0;
  }


  setPendingFocusPopupId(identificationId: string | null): void {
    this.pendingFocusPopupId = identificationId;
  }

  /** General GIS layer panel: z-order panes, cluster accent, polygon fill opacity. Pass `null` to reset. */
  configureGeneralGisLayerStyle(options: {
    color?: string | null;
    opacityPercent?: number | null;
    polygonPane?: string | null;
    clusterPane?: string | null;
  }): void {
    if ('polygonPane' in options) {
      this.generalGisPolygonPane = options.polygonPane ?? null;
    }
    if ('clusterPane' in options) {
      this.generalGisClusterPane = options.clusterPane ?? null;
    }
    if ('color' in options) {
      this.layerAccentColor = options.color ?? null;
    }
    if ('opacityPercent' in options) {
      this.polygonFillOpacityPercent = options.opacityPercent ?? null;
    }
    if (this.cropIaData$.value && this.mapInstance) {
      this.updateDisplay();
    }
  }

  private scaledFillOpacity(base: number): number {
    if (this.polygonFillOpacityPercent == null) {
      return base;
    }
    const scale = Math.max(0, Math.min(100, this.polygonFillOpacityPercent)) / 100;
    return base * scale;
  }

  private withGeneralGisPolygonStyle(polygon: GisPolygon): GisPolygon {
    if (!this.generalGisPolygonPane) {
      return polygon;
    }
    return {
      ...polygon,
      pane: this.generalGisPolygonPane,
      fillOpacity: this.scaledFillOpacity(polygon.fillOpacity ?? 0.7),
    };
  }

  /**
   * Initialize the manager with a map instance
   */
  initialize(mapInstance: OpenStreetMapComponent): void {
    this.mapInstance = mapInstance;
    this.cropIaDataSub?.unsubscribe();
    this.cropIaDataSub = null;
    this.langChangeSub?.unsubscribe();
    this.langChangeSub = null;

    this.cropIaDataSub = this.cropIaData$.subscribe(() => {
      this.updateDisplay();
    });
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.updateIaPolygonLabels();
    });
  }

  /**
   * Load crop identification by bounding box and zoom
   */
  loadCropIaByBbox(params: {
    minLon: number;
    minLat: number;
    maxLon: number;
    maxLat: number;
    zoom: number;
    query?: {filters?: unknown[]};
  }): Observable<CropIdentificationMapResponse> {
    this.clearIaLayers();
    return this.cropIdentificationService.aiInferenceCropIdentificationGetCropIdentificationByBboxPost(params as never)
      .pipe(
        tap((response: CropIdentificationMapResponse) => {
          this.cropIaData$.next(response);
          return response;
        }));
  }

  /** Render API response: polygons when items exist, otherwise clusters. */
  private updateDisplay(): void {
    if (!this.mapInstance) return;
    const data = this.cropIaData$.value;

    this.clearIaLayers();

    const items: CropIdentificationMapItemDto[] = (data?.items && Array.isArray(data.items)) ? data.items : [];
    const hasItems: boolean = items.length > 0;

    if (hasItems) {
      this.renderCropPolygons(items);
      return;
    }

    if (data?.clusters && Array.isArray(data.clusters)) {
      this.renderClusterMarkers(data.clusters);
    }
  }

  /**
   * Render cluster circle markers
   */
  private renderClusterMarkers(clusters: MapClusterDto[]): void {
    if (!this.mapInstance) return;
    const gisCircles = clusters.map((cluster, id) => {

      // @ts-ignore
      const marker: GisCircleMarker = {
        id: `crop-cluster-${id}`,
        center: [cluster.lat as number, cluster.lon as number],
        radius: 10,
        count: cluster.count as number,
        fillColor: this.layerAccentColor ?? '#A020F0',
        strokeColor: this.layerAccentColor ?? '#A020F0',
        pane: this.generalGisClusterPane ?? undefined,
        fillOpacity: 0.7,
        strokeWidth: 2,
        type: 'crop-cluster',
        data: {
          ...cluster,
          regionName: cluster.localizedNames ? cluster.localizedNames[this.translate.currentLang] || Object.values(cluster.localizedNames)[0] : 'Cluster',
          regionId: (cluster as { regionId?: string }).regionId ?? '',
          fieldCount: cluster.count as number,
        },
        onClick: (circle) => {
          this.cropIaMarkerClick$.next({
            lat: circle.center[0],
            lng: circle.center[1],
            data: cluster,
          });
          const layerController = this.mapInstance?.getLayersController()

          if (!layerController) {
            return
          }
          layerController.centerToPoint(circle.center[0] as number, circle.center[1] as number, 14)

        }
      };
      this.managedMarkerIds.add(marker.id);
      this.managedClusters.add(marker.id);
      return marker
    });

    this.mapInstance?.mapController$.next({addCircleMarkers: gisCircles});
  }

  /**
   * Render crop polygons for items
   */
  private renderCropPolygons(items: CropIdentificationMapItemDto[]): void {
    if (!this.mapInstance) return;
    const gisPolygons: GisPolygon[] = [];
    const statusMarkers: Marker[] = [];

    for (const item of items) {
      if (!item.polygonJson) {
        continue;
      }
      const coordinates = PolygonConverterUtil.polygonJsonToLatLngFlat(item.polygonJson) as [number, number][];
      if (!coordinates?.length) {
        continue;
      }

      const classes = this.parseCandidatesClasses(item);
      const first = classes[0];
      const cropId = item.cropId as string | undefined;

      // Detail zoom: compare field cropId vs first AI candidate id (same hatch idea as season plan vs fact).
      if (item.candidatesJson && first?.id && cropId) {
        const firstId = String(first.id);
        const factId = String(cropId);
        const match = factId === firstId;
        const factColor = this.cropCacheService.getCropColorById(factId, this.NO_CROP_POLYGON_COLOR);
        const firstCandColor = this.cropCacheService.getCropColorById(firstId, this.NO_CROP_POLYGON_COLOR);
        const factName = this.cropCacheService.getCropNameById(factId, true) || '';
        const firstName = (this.cropCacheService.getCropNameById(firstId, true) as string) || '';
        const prob = typeof first.score === 'number' ? first.score : 0;
        const composedLabel = this.shouldShowIaLabel(item, prob)
          ? this.buildPlannedVsIaLabel(factName, firstName, prob)
          : '';

        const polygon: GisPolygon = {
          id: `crop-ia-polygon-${item.id}`,
          name: factName || firstName || 'Crop',
          coordinates,
          fillColor: factColor,
          fillOpacity: match ? 0.75 : 0.8,
          strokeColor: match ? this.MATCH_STROKE : this.MISMATCH_STROKE,
          strokeWidth: 3,
          fill: true,
          label: composedLabel,
          popup: composedLabel || undefined,
          openPopupOnHover: false,
          planFactDiagonalStripes: match
            ? undefined
            : {
                factFillColor: factColor,
                planStripeColor: firstCandColor,
                stripeStrokeWidth: 4,
                backgroundFillOpacity: 0.88,
                stripeStrokeOpacity: 1,
                stripeDashArray: '4 6',
              },
          data: {
            ...item,
            cropName: factName || firstName,
            probability: prob,
            iaFirstCandidate: first,
            factVsFirstCandidateMatch: match,
          },
        };
        this.managedPolygonIds.add(polygon.id);
        gisPolygons.push(this.withGeneralGisPolygonStyle(polygon));

        const center = this.getCropIaStatusMarkerLatLng(item, coordinates);
        if (center) {
          const markerId = `crop-ia-status-${item.id}`;
          const dotColor = match ? this.MATCH_STROKE : this.MISMATCH_STROKE;
          statusMarkers.push({
            id: markerId,
            lat: center[0],
            lng: center[1],
            withCluster: false,
            clusterGroupId: this.clusterGroupId ?? undefined,
            popup: composedLabel || undefined,
            openPopupOnHover: false,
            customIcon: `<div style="width:14px;height:14px;border-radius:50%;background:${dotColor};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.45);"></div>`,
            customIconSize: [14, 14],
            customIconAnchor: [7, 7],
            title: composedLabel,
            data: {item, match, firstId, factId},
            onClick: () => {
              this.cropIaPolygonClick$.next({
                fillColor: dotColor,
                lat: center[0],
                lng: center[1],
                data: item,
              });
            },
          });
          this.managedMarkerIds.add(markerId);
        }
        continue;
      }

      if (classes.length > 0 && first) {
        const firstId = String(first.id);
        const fillColor = this.cropCacheService.getCropColorById(firstId, this.NO_CROP_POLYGON_COLOR);
        const cropName = (this.cropCacheService.getCropNameById(firstId, true) as string) || 'Crop';
        const prob = typeof first.score === 'number' ? first.score : 0;
        const composedLabel = this.shouldShowIaLabel(item, prob)
          ? this.buildPlannedVsIaLabel('', cropName, prob)
          : '';
        const polygon: GisPolygon = {
          id: `crop-ia-polygon-${item.id}`,
          name: cropName,
          coordinates,
          fillColor,
          fillOpacity: 0.65,
          strokeColor: '#ffffff',
          strokeWidth: 2,
          fill: true,
          label: composedLabel,
          popup: composedLabel || undefined,
          openPopupOnHover: false,
          data: {
            ...item,
            cropName,
            probability: prob,
            iaFirstCandidate: first,
          },
        };
        this.managedPolygonIds.add(polygon.id);
        gisPolygons.push(this.withGeneralGisPolygonStyle(polygon));
        continue;
      }

      const cropColor = item.cropId
        ? this.cropCacheService.getCropColorById(String(item.cropId), this.NO_CROP_POLYGON_COLOR)
        : this.NO_CROP_POLYGON_COLOR;
      const cropName = item.cropId
        ? (this.cropCacheService.getCropNameById(String(item.cropId), true) as string)
        : '';

      const composedLabel = this.shouldShowIaLabel(item, null)
        ? this.buildPlannedVsIaLabel(cropName, '')
        : '';
      const polygon: GisPolygon = {
        id: `crop-ia-polygon-${item.id}`,
        name: cropName || 'Crop',
        coordinates,
        fillColor: cropColor,
        fillOpacity: 0.7,
        strokeColor: '#ffffff',
        strokeWidth: 2,
        fill: true,
        label: composedLabel,
        popup: composedLabel || cropName || undefined,
        openPopupOnHover: false,
        data: item,
      };
      this.managedPolygonIds.add(polygon.id);
      gisPolygons.push(this.withGeneralGisPolygonStyle(polygon));
    }

    this.mapInstance.mapController$.next({addGisPolygons: gisPolygons});
    if (statusMarkers.length) {
      this.mapInstance.mapController$.next({mergeMarkers: statusMarkers});
    }
    this.schedulePendingFocusPopupOpen();
  }

  private schedulePendingFocusPopupOpen(): void {
    const focusId = this.pendingFocusPopupId;
    if (!focusId || !this.mapInstance) {
      return;
    }
    const tryOpen = (attempt: number) => {
      if (!this.pendingFocusPopupId || this.pendingFocusPopupId !== focusId) {
        return;
      }
      const lc = this.mapInstance?.getLayersController();
      if (!lc) {
        return;
      }
      const markerId = `crop-ia-status-${focusId}`;
      const polygonId = `crop-ia-polygon-${focusId}`;
      if (lc.openMarkerPopup(markerId) || lc.openPolygonPopup(polygonId)) {
        this.pendingFocusPopupId = null;
        return;
      }
      if (attempt < 30) {
        setTimeout(() => tryOpen(attempt + 1), 150);
      } else {
        this.pendingFocusPopupId = null;
      }
    };
    setTimeout(() => tryOpen(0), 50);
  }

  /** API `candidatesJson`: `{ "classes": [{ id, name, score }, ...] }`. */
  private parseCandidatesClasses(item: CropIdentificationMapItemDto): { id: string; name?: string; score?: number }[] {
    const raw = (item as { candidatesJson?: string | { classes?: unknown[] } })?.candidatesJson;
    if (raw == null) {
      return [];
    }
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw as string) : raw;
      const classes = parsed?.classes;
      if (!Array.isArray(classes)) {
        return [];
      }
      return classes
        .map((c: any) => ({
          id: c?.id != null ? String(c.id) : '',
          name: c?.name,
          score: typeof c?.score === 'number' ? c.score : typeof c?.prob === 'number' ? c.prob : undefined,
        }))
        .filter((c) => !!c.id);
    } catch {
      return [];
    }
  }

  private getCropIaStatusMarkerLatLng(
    item: CropIdentificationMapItemDto,
    coordinates: [number, number][]
  ): [number, number] | null {
    const anyItem = item as { latitude?: number; longitude?: number };
    if (
      typeof anyItem.latitude === 'number' &&
      typeof anyItem.longitude === 'number' &&
      !Number.isNaN(anyItem.latitude) &&
      !Number.isNaN(anyItem.longitude)
    ) {
      return [anyItem.latitude, anyItem.longitude];
    }
    return this.polygonCentroid(coordinates);
  }

  private polygonCentroid(coords: [number, number][]): [number, number] | null {
    if (!coords?.length) {
      return null;
    }
    let lat = 0;
    let lng = 0;
    let n = 0;
    for (const c of coords) {
      if (!c || c.length < 2) {
        continue;
      }
      lat += c[0];
      lng += c[1];
      n++;
    }
    return n ? [lat / n, lng / n] : null;
  }

  /**
   * Clear IA layers from map
   */
  clearIaLayers(): void {
    if (!this.mapInstance) return;
    // Remove all IA markers
    const markerIds = Array.from(this.managedMarkerIds);


    this.mapInstance.mapController$.next({removeMarkers: markerIds});
    markerIds.forEach(id => this.managedMarkerIds.delete(id));
    // Remove all clusters
    const circleIds: string[] = Array.from(this.managedClusters);
    circleIds.forEach(id => {
      this.mapInstance?.mapController$.next({removeCircleMarker: {id}});
      this.managedClusters.delete(id);
    });
    // Remove all IA polygons
    const polygonIds = Array.from(this.managedPolygonIds);
    this.mapInstance.mapController$.next({removePolygons: polygonIds as string[]});
    polygonIds.forEach(id => this.managedPolygonIds.delete(id));
  }

  /**
   * Update IA polygon labels when the language changes
   */
  private updateIaPolygonLabels(): void {
    if (!this.mapInstance) return;
    const entries = this.getDisplayedIaPolygons();
    const iaIdSet = this.managedPolygonIds;
    entries.forEach((entry: { polygon?: any; properties?: any }) => {
      const props = entry.properties;
      const shape = entry.polygon;
      if (!props?.id || !iaIdSet.has(String(props.id)) || !props.data) {
        return;
      }
      const data = props.data;
      const first = data?.iaFirstCandidate as { id?: string; name?: string; score?: number } | undefined;
      const factCropId = data?.cropId as string | undefined;
      let factName = factCropId
        ? ((this.cropCacheService.getCropNameById(String(factCropId), true) as string) || '')
        : '';
      const firstName =
        first?.id != null ? (this.cropCacheService.getCropNameById(String(first.id), true) as string) : '';
      const prob =
        data?.probability ??
        (typeof first?.score === 'number' ? first.score : data?.prob != null ? Number(data.prob) : null);

      let plannedName = factName;
      let iaName = firstName;
      if (!plannedName && !iaName) {
        let cropName = factName || props.name || '';
        let cropId = factCropId;
        if (!cropId && data?.cropName) {
          cropId = this.cropCacheService.cachedCropsData.find(crop => {
            return crop.names && Object.values(crop.names).some(name => name === data.cropName);
          })?.id;
        }
        if (cropId) {
          cropName = (this.cropCacheService.getCropNameById(cropId, true) as string) || cropName;
        }
        plannedName = cropName;
      }
      const probNum =
        prob !== null && prob !== undefined && !Number.isNaN(Number(prob))
          ? Number(prob)
          : undefined;
      const newLabel = this.shouldShowIaLabel(data as CropIdentificationMapItemDto, probNum)
        ? this.buildPlannedVsIaLabel(plannedName, iaName, probNum)
        : '';
      props.label = newLabel;

      const labelMarker = shape?._labelMarker;
      const el: HTMLElement | null = labelMarker?.getElement?.() ?? null;
      if (el) {
        const textDiv = (el.querySelector('div') as HTMLElement | null) ?? el;
        textDiv.innerHTML = newLabel;
      }
    });
  }

  /**
   * Get all currently displayed IA polygons on the map
   */
  private getDisplayedIaPolygons(): any[] {
    if (!this.mapInstance || !this.mapInstance.getLayersController()) return [];
    return this.mapInstance.getLayersController().getAllPolygons?.() || [];
  }
}
